import type { KeyboardEvent, RefObject } from 'react'

import { resizeRectangleFromHandle } from './markupEditorHandles'
import { replacePolygonVertex } from './markupEditorPolygon'
import { annotationGeometryEqual, translateGeometry } from './markupGeometry'
import type { AnnotationGeometry, MarkupAnnotation } from './types'
import type { useMarkupEditorInteraction } from './useMarkupEditorInteraction'

type KeyboardEditTarget =
  | { kind: 'move' }
  | { kind: 'vertex'; index: number }
  | { kind: 'handle'; value: string }
type EditorInteraction = Pick<
  ReturnType<typeof useMarkupEditorInteraction>,
  | 'key'
  | 'updateGeometry'
  | 'submitDelete'
  | 'completePolygon'
  | 'cancelFromKeyboard'
  | 'deleteSelectedVertex'
  | 'focusAnnotation'
  | 'insertVertex'
  | 'selectedVertex'
  | 'rectangleConstraints'
  | 'minimumRectangleSize'
  | 'constrainRectangleTransition'
>

interface MarkupEditorKeyboardOptions {
  interaction: EditorInteraction
  annotations: ReadonlyArray<MarkupAnnotation>
  selectedAnnotationId?: string
  pageFrameRef: RefObject<HTMLDivElement | null>
  svgRef: RefObject<SVGSVGElement | null>
  polygonVertexCount: number
}

export function useMarkupEditorKeyboard({
  interaction,
  annotations,
  selectedAnnotationId,
  pageFrameRef,
  svgRef,
  polygonVertexCount,
}: MarkupEditorKeyboardOptions) {
  const handleKeyboardEdit = (
    event: KeyboardEvent<HTMLDivElement>,
    annotation: MarkupAnnotation,
    target: KeyboardEditTarget
  ) => {
    if (
      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
    )
      return
    if (event.altKey || event.ctrlKey || event.metaKey) return

    const amount = event.shiftKey ? 0.05 : 0.01
    const deltaX =
      event.key === 'ArrowLeft'
        ? -amount
        : event.key === 'ArrowRight'
          ? amount
          : 0
    const deltaY =
      event.key === 'ArrowUp' ? -amount : event.key === 'ArrowDown' ? amount : 0
    let next: AnnotationGeometry | null = null

    if (target.kind === 'move') {
      next = translateGeometry(
        annotation.geometry,
        { x: 0, y: 0 },
        { x: deltaX, y: deltaY },
        Boolean(interaction.rectangleConstraints)
      )
    } else if (
      target.kind === 'vertex' &&
      annotation.geometry.shape === 'polygon'
    ) {
      const index = target.index
      const vertex = annotation.geometry.vertices[index]
      if (vertex)
        next = replacePolygonVertex(annotation.geometry, index, {
          x: vertex.x + deltaX,
          y: vertex.y + deltaY,
        })
    } else if (
      target.kind === 'handle' &&
      annotation.geometry.shape === 'rectangle'
    ) {
      next = resizeRectangleFromHandle(
        annotation.geometry,
        target.value,
        deltaX,
        deltaY,
        interaction.rectangleConstraints
          ? interaction.minimumRectangleSize
          : undefined,
        Boolean(interaction.rectangleConstraints)
      )
    }

    if (
      next?.shape === 'rectangle' &&
      annotation.geometry.shape === 'rectangle' &&
      interaction.rectangleConstraints
    )
      next = interaction.constrainRectangleTransition(annotation.geometry, next)

    if (
      next &&
      !annotationGeometryEqual(next, annotation.geometry) &&
      interaction.updateGeometry(
        annotation.id,
        annotation.geometry,
        next,
        interaction.key
      ) === 'updated'
    ) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target
    const pageFrame = pageFrameRef.current
    if (!(target instanceof Node) || !pageFrame?.contains(target)) return

    const element = target instanceof Element ? target : null
    const annotationId = element?.getAttribute('data-annotation-id')
    const annotation = annotations.find((item) => item.id === annotationId)
    const selectedAnnotation = annotations.find(
      (item) => item.id === selectedAnnotationId
    )
    const vertexIndex = element?.getAttribute('data-edit-vertex')
    const edgeIndex = element?.getAttribute('data-edge-index')
    const handle = element?.getAttribute('data-edit-handle')
    const isShape = element?.getAttribute('data-annotation-shape') === 'true'
    const isEditTarget = Boolean(
      isShape || vertexIndex !== null || edgeIndex !== null || handle
    )
    const isDisabled = element?.getAttribute('aria-disabled') === 'true'
    const handleKeyAllowed =
      Boolean(handle) &&
      (event.key === 'Escape' || event.key.startsWith('Arrow'))
    const isProtectedControl =
      target instanceof HTMLElement &&
      (target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
        (target.tagName === 'BUTTON' && !handleKeyAllowed))
    if (isProtectedControl) return

    if (annotation && isEditTarget && !isDisabled) {
      if (isShape && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault()
        event.stopPropagation()
        interaction.focusAnnotation(annotation)
        return
      }
      if (edgeIndex !== null) {
        if (event.key === 'Enter' || event.key === ' ') {
          const index = Number(edgeIndex)
          const geometry = annotation.geometry
          if (geometry.shape === 'polygon') {
            const vertex = geometry.vertices[index]
            const next =
              geometry.vertices[(index + 1) % geometry.vertices.length]
            if (vertex && next) {
              event.preventDefault()
              event.stopPropagation()
              interaction.insertVertex(
                annotation,
                index,
                {
                  x: (vertex.x + next.x) / 2,
                  y: (vertex.y + next.y) / 2,
                },
                'page'
              )
            }
          }
          return
        }
        if (event.key.startsWith('Arrow')) {
          event.stopPropagation()
          return
        }
      } else if (vertexIndex !== null) {
        handleKeyboardEdit(event, annotation, {
          kind: 'vertex',
          index: Number(vertexIndex),
        })
      } else if (handle) {
        handleKeyboardEdit(event, annotation, { kind: 'handle', value: handle })
      } else if (isShape) {
        handleKeyboardEdit(event, annotation, { kind: 'move' })
      }
    }

    const focusedEditHandle =
      element?.hasAttribute('data-edit-handle') ||
      element?.hasAttribute('data-edit-vertex')

    if (event.key === 'Escape') {
      interaction.cancelFromKeyboard()
    } else if (
      event.key === 'Enter' &&
      event.target === svgRef.current &&
      polygonVertexCount >= 3
    ) {
      interaction.completePolygon()
    } else if (
      event.key === 'Delete' &&
      interaction.selectedVertex !== undefined
    ) {
      if (interaction.deleteSelectedVertex()) event.preventDefault()
      return
    } else if (
      (event.key === 'Delete' ||
        (event.key === 'Backspace' &&
          selectedAnnotation?.geometry.shape === 'rectangle')) &&
      selectedAnnotationId &&
      !focusedEditHandle
    ) {
      if (event.key === 'Backspace') event.preventDefault()
      interaction.submitDelete(selectedAnnotationId, interaction.key)
    }
  }

  return { handleKeyDown }
}
